import { useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { LockKey, X, CheckCircle, Warning, Eye, EyeSlash } from "@phosphor-icons/react";

/**
 * Password strength checker (mirrors backend rules).
 * Returns { ok, checks: [{label, pass}] }.
 */
function checkStrength(pwd) {
  const checks = [
    { label: "Pelo menos 8 caracteres", pass: pwd.length >= 8 },
    { label: "1 letra maiúscula", pass: /[A-Z]/.test(pwd) },
    { label: "1 número", pass: /\d/.test(pwd) },
  ];
  return { ok: checks.every((c) => c.pass), checks };
}

export default function ChangePasswordModal({ open, onClose }) {
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [showCur, setShowCur] = useState(false);
  const [showNext, setShowNext] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const { ok: strong, checks } = checkStrength(next);
  const matches = next.length > 0 && next === confirm;

  if (!open) return null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!strong) return toast.error("A nova senha não atende aos requisitos mínimos.");
    if (!matches) return toast.error("A confirmação não bate com a nova senha.");
    setSubmitting(true);
    try {
      await api.post("/auth/change-password", {
        current_password: current,
        new_password: next,
      });
      toast.success("Senha alterada com sucesso!");
      setCurrent(""); setNext(""); setConfirm("");
      onClose();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erro ao alterar senha");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[60] bg-black/60 flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="change-password-modal"
    >
      <div
        className="bg-white dark:bg-[#111827] w-full max-w-md p-6 border-2 border-[#0F4C3A] shadow-brutal"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-5">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-[#0F4C3A] text-white flex items-center justify-center">
              <LockKey size={20} weight="bold" />
            </div>
            <h2 className="text-xl font-black tracking-tight dark:text-white">Alterar senha</h2>
          </div>
          <button onClick={onClose} data-testid="change-password-close" className="text-[#5C5C59] hover:text-black dark:hover:text-white">
            <X size={22} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label className="text-xs font-semibold uppercase tracking-widest dark:text-[#c8d5cf]">Senha atual</Label>
            <div className="relative mt-1">
              <Input
                type={showCur ? "text" : "password"}
                value={current}
                onChange={(e) => setCurrent(e.target.value)}
                required
                autoComplete="current-password"
                data-testid="cp-current"
                className="rounded-none pr-10"
              />
              <button type="button" onClick={() => setShowCur((v) => !v)} className="absolute right-2 top-1/2 -translate-y-1/2 text-[#5C5C59]">
                {showCur ? <EyeSlash size={16} /> : <Eye size={16} />}
              </button>
            </div>
          </div>

          <div>
            <Label className="text-xs font-semibold uppercase tracking-widest dark:text-[#c8d5cf]">Nova senha</Label>
            <div className="relative mt-1">
              <Input
                type={showNext ? "text" : "password"}
                value={next}
                onChange={(e) => setNext(e.target.value)}
                required
                autoComplete="new-password"
                data-testid="cp-new"
                className="rounded-none pr-10"
              />
              <button type="button" onClick={() => setShowNext((v) => !v)} className="absolute right-2 top-1/2 -translate-y-1/2 text-[#5C5C59]">
                {showNext ? <EyeSlash size={16} /> : <Eye size={16} />}
              </button>
            </div>
            {next.length > 0 && (
              <ul className="mt-2 space-y-1">
                {checks.map((c, i) => (
                  <li key={i} className={`flex items-center gap-2 text-xs ${c.pass ? "text-[#0F4C3A]" : "text-[#B91C1C]"}`}>
                    {c.pass ? <CheckCircle size={14} weight="fill" /> : <Warning size={14} weight="fill" />}
                    {c.label}
                  </li>
                ))}
              </ul>
            )}
          </div>

          <div>
            <Label className="text-xs font-semibold uppercase tracking-widest dark:text-[#c8d5cf]">Confirmar nova senha</Label>
            <Input
              type={showNext ? "text" : "password"}
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              required
              autoComplete="new-password"
              data-testid="cp-confirm"
              className="rounded-none mt-1"
            />
            {confirm.length > 0 && (
              <p className={`mt-1 text-xs flex items-center gap-1 ${matches ? "text-[#0F4C3A]" : "text-[#B91C1C]"}`}>
                {matches ? <CheckCircle size={12} weight="fill" /> : <Warning size={12} weight="fill" />}
                {matches ? "As senhas coincidem" : "As senhas não coincidem"}
              </p>
            )}
          </div>

          <div className="flex gap-3 pt-2">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              className="flex-1 rounded-none border-[#5C5C59] text-[#5C5C59]"
              data-testid="cp-cancel"
            >
              Cancelar
            </Button>
            <Button
              type="submit"
              disabled={submitting || !strong || !matches || !current}
              className="flex-1 rounded-none bg-[#0F4C3A] hover:bg-[#0A3629] text-white disabled:opacity-40"
              data-testid="cp-submit"
            >
              {submitting ? "Salvando…" : "Alterar senha"}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
